import { LearnMoreLink } from '@auctisync/ui';
import type { Tables, TablesUpdate } from '@auctisync/shared';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useSession } from '../../hooks/useSession';
import { usePushSubscription } from '../../hooks/usePushSubscription';
import { supabase } from '../../lib/supabase';

type Profile = Tables<'profiles'>;

type FormState = {
  display_name: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
  email_outbid: boolean;
  email_ending_soon: boolean;
  email_winner: boolean;
  email_follow_updates: boolean;
  email_question_answered: boolean;
  email_smart_alert: boolean;
  push_outbid: boolean;
  push_ending_soon: boolean;
  push_winner: boolean;
  push_invoice: boolean;
  push_follow_updates: boolean;
  push_question_answered: boolean;
  push_smart_alert: boolean;
  show_exposure_meter: boolean;
};

function profileToForm(p: Profile | null, email: string): FormState {
  return {
    display_name: p?.display_name ?? email.split('@')[0] ?? '',
    phone: p?.phone ?? '',
    address_line1: p?.address_line1 ?? '',
    address_line2: p?.address_line2 ?? '',
    city: p?.city ?? '',
    region: p?.region ?? '',
    postal_code: p?.postal_code ?? '',
    country: p?.country ?? 'US',
    email_outbid: p?.email_outbid ?? true,
    email_ending_soon: p?.email_ending_soon ?? true,
    email_winner: p?.email_winner ?? true,
    email_follow_updates: p?.email_follow_updates ?? true,
    email_question_answered: p?.email_question_answered ?? true,
    email_smart_alert: p?.email_smart_alert ?? true,
    push_outbid: p?.push_outbid ?? true,
    push_ending_soon: p?.push_ending_soon ?? true,
    push_winner: p?.push_winner ?? true,
    push_invoice: p?.push_invoice ?? true,
    push_follow_updates: p?.push_follow_updates ?? true,
    push_question_answered: p?.push_question_answered ?? true,
    push_smart_alert: p?.push_smart_alert ?? true,
    show_exposure_meter: p?.show_exposure_meter ?? false,
  };
}

function formToPatch(form: FormState): TablesUpdate<'profiles'> {
  const s = (v: string) => (v.trim() === '' ? null : v.trim());
  return {
    display_name: form.display_name.trim() || null,
    phone: s(form.phone),
    address_line1: s(form.address_line1),
    address_line2: s(form.address_line2),
    city: s(form.city),
    region: s(form.region),
    postal_code: s(form.postal_code),
    country: form.country.trim() || 'US',
    email_outbid: form.email_outbid,
    email_ending_soon: form.email_ending_soon,
    email_winner: form.email_winner,
    email_follow_updates: form.email_follow_updates,
    email_question_answered: form.email_question_answered,
    email_smart_alert: form.email_smart_alert,
    push_outbid: form.push_outbid,
    push_ending_soon: form.push_ending_soon,
    push_winner: form.push_winner,
    push_invoice: form.push_invoice,
    push_follow_updates: form.push_follow_updates,
    push_question_answered: form.push_question_answered,
    push_smart_alert: form.push_smart_alert,
    show_exposure_meter: form.show_exposure_meter,
  };
}

export function BidderProfile() {
  const { session, loading: sessionLoading, userId } = useSession();
  // Strings, so the form is read once per visitor — not again on every return
  // to the tab, which used to wipe what the bidder was typing (see useSession).
  const email = session?.user.email ?? '';
  const toast = useToast();
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else {
        setForm(profileToForm(data, email));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, email]);

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form || !session) return;
    setError(null);
    setSaving(true);

    const patch = formToPatch(form);
    // .update + .eq instead of .upsert because the profiles table has no
    // INSERT policy — only SELECT + UPDATE. .upsert always triggers the
    // INSERT-policy check (even when the row exists) and fails with
    // "new row violates row-level security policy". The handle_new_user
    // trigger creates the row at signup, so we only ever need UPDATE here.
    const { error: updateErr } = await supabase
      .from('profiles')
      .update(patch)
      .eq('id', session.user.id);
    setSaving(false);
    if (updateErr) setError(updateErr.message);
    else toast.success('Saved.');
  }

  if (sessionLoading || loading || !form) {
    return <p className="muted">Loading…</p>;
  }
  if (!session) return <Navigate to="/admin" replace />;

  return (
    <>
      <div className="wa-hd">
        <div>
          <span className="w-lbl">Settings</span>
          <h1 className="w-h1" style={{ fontSize: 32 }}>
            Account &amp; preferences
          </h1>
          <p className="muted" style={{ fontSize: 14, marginBottom: '0.6rem' }}>
            Contact info and email preferences. Email is managed in auth.
          </p>
          <LearnMoreLink helpSlug="profile-and-preferences" newTab>
            What auction houses see
          </LearnMoreLink>
          <div style={{ marginTop: '0.7rem' }}>
            <Link to="/auth/reset-password" style={{ fontSize: '0.875rem' }}>
              Change password →
            </Link>
          </div>
        </div>
      </div>

      {error && <AlertBox variant="danger">{error}</AlertBox>}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        style={{ display: 'grid', gap: 22 }}
      >
        <Section title="Identity">
          <Field label="Email">
            <span className="w-field" style={{ opacity: 0.75 }}>
              <input type="email" value={session.user.email ?? ''} disabled />
            </span>
          </Field>
          <Field label="Display name">
            <span className="w-field">
              <input
                type="text"
                value={form.display_name}
                onChange={(e) => setField('display_name', e.target.value)}
              />
            </span>
          </Field>
          <Field label="Phone">
            <span className="w-field">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
              />
            </span>
          </Field>
        </Section>

        <Section title="Address (used on invoices)">
          <TwoCol>
            <Field label="Address line 1">
              <span className="w-field">
                <input
                  type="text"
                  value={form.address_line1}
                  onChange={(e) => setField('address_line1', e.target.value)}
                />
              </span>
            </Field>
            <Field label="Address line 2">
              <span className="w-field">
                <input
                  type="text"
                  value={form.address_line2}
                  onChange={(e) => setField('address_line2', e.target.value)}
                />
              </span>
            </Field>
            <Field label="City">
              <span className="w-field">
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setField('city', e.target.value)}
                />
              </span>
            </Field>
            <Field label="State / region">
              <span className="w-field">
                <input
                  type="text"
                  value={form.region}
                  onChange={(e) => setField('region', e.target.value)}
                />
              </span>
            </Field>
            <Field label="Postal code">
              <span className="w-field">
                <input
                  type="text"
                  value={form.postal_code}
                  onChange={(e) => setField('postal_code', e.target.value)}
                />
              </span>
            </Field>
            <Field label="Country">
              <span className="w-field">
                <input
                  type="text"
                  value={form.country}
                  onChange={(e) => setField('country', e.target.value)}
                />
              </span>
            </Field>
          </TwoCol>
        </Section>

        <Section title="Email preferences">
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            These toggles are saved now; the actual emails ship with Module #8.
          </p>
          <Toggle
            label="Email me when I'm outbid"
            checked={form.email_outbid}
            onChange={(v) => setField('email_outbid', v)}
          />
          <Toggle
            label="Email me when lots I'm winning are closing soon"
            checked={form.email_ending_soon}
            onChange={(v) => setField('email_ending_soon', v)}
          />
          <Toggle
            label="Email me when I win a lot"
            checked={form.email_winner}
            onChange={(v) => setField('email_winner', v)}
          />
          <Toggle
            label="Email me about follow updates (auctions I follow going live or ending soon; new auctions from sellers I follow)"
            checked={form.email_follow_updates}
            onChange={(v) => setField('email_follow_updates', v)}
          />
          <Toggle
            label="Email me when a seller answers my question on a lot"
            checked={form.email_question_answered}
            onChange={(v) => setField('email_question_answered', v)}
          />
          <Toggle
            label="Email me when one of my smart alerts fires"
            checked={form.email_smart_alert}
            onChange={(v) => setField('email_smart_alert', v)}
          />
        </Section>

        <Section title="Live auction room">
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            The exposure meter shows a running total of what you&rsquo;re on the hook for while a
            live auction is closing — winning-now and your max exposure, with buyer&rsquo;s premium
            included. Off by default.
          </p>
          <Toggle
            label="Show the exposure meter in the live auction room"
            checked={form.show_exposure_meter}
            onChange={(v) => setField('show_exposure_meter', v)}
          />
        </Section>

        <Section title="Push notifications on this device">
          <PushNotificationsControl />
        </Section>

        <Section title="Push event preferences">
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Control which events trigger a push notification when push is enabled. Transactional
            events (approval requests, account communications) always notify.
          </p>
          <Toggle
            label="Push me when I'm outbid"
            checked={form.push_outbid}
            onChange={(v) => setField('push_outbid', v)}
          />
          <Toggle
            label="Push me when lots I'm winning are closing soon"
            checked={form.push_ending_soon}
            onChange={(v) => setField('push_ending_soon', v)}
          />
          <Toggle
            label="Push me when I win a lot"
            checked={form.push_winner}
            onChange={(v) => setField('push_winner', v)}
          />
          <Toggle
            label="Push me when an invoice is ready, due, or voided"
            checked={form.push_invoice}
            onChange={(v) => setField('push_invoice', v)}
          />
          <Toggle
            label="Push me about follow updates (auctions I follow going live or ending soon; new auctions from sellers I follow)"
            checked={form.push_follow_updates}
            onChange={(v) => setField('push_follow_updates', v)}
          />
          <Toggle
            label="Push me when a seller answers my question on a lot"
            checked={form.push_question_answered}
            onChange={(v) => setField('push_question_answered', v)}
          />
          <Toggle
            label="Push me when one of my smart alerts fires"
            checked={form.push_smart_alert}
            onChange={(v) => setField('push_smart_alert', v)}
          />
        </Section>

        <div
          style={{
            display: 'flex',
            gap: 12,
            justifyContent: 'flex-end',
            paddingTop: 12,
            borderTop: '1px solid var(--border)',
          }}
        >
          <Link to="/my/dashboard" className="w-btn w-btn-out">
            Cancel
          </Link>
          <button type="submit" className="w-btn w-btn-cta" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </>
  );
}

function PushNotificationsControl() {
  const { support, permission, isSubscribed, isPending, error, subscribe, unsubscribe } =
    usePushSubscription();

  // Capability gate — surface a specific message per failure reason so the
  // bidder knows whether it's a browser issue, a config issue, or an iOS
  // platform constraint they can resolve by installing as a PWA.
  if (!support.supported) {
    const message: Record<typeof support.reason, string> = {
      no_service_worker: "Push notifications aren't supported in this browser.",
      no_push_manager: "Push notifications aren't supported in this browser.",
      no_notifications: "Push notifications aren't supported in this browser.",
      no_vapid_key: "Push notifications aren't configured for this environment yet.",
      ios_needs_pwa_install:
        'On iPhone or iPad, push notifications work only when AuctiSync is installed as a Home Screen app. Open this site in Safari, tap the Share icon at the bottom, choose Add to Home Screen, then open AuctiSync from the new home-screen icon.',
    };
    return (
      <p className="muted" style={{ fontSize: 14, margin: 0 }}>
        {message[support.reason]}
      </p>
    );
  }

  // Browser-level deny — user has to lift it themselves in site settings.
  if (permission === 'denied') {
    return (
      <p className="muted" style={{ fontSize: 14, margin: 0 }}>
        Push notifications are blocked. Re-enable them in your browser&rsquo;s site settings for
        this site, then refresh.
      </p>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p className="muted" style={{ fontSize: 14, margin: 0 }}>
        {isSubscribed
          ? 'You will receive outbid alerts, ending-soon reminders, and win notifications on this device — even when AuctiSync is closed.'
          : 'Get outbid alerts, ending-soon reminders, and win notifications on this device — even when AuctiSync is closed.'}
      </p>
      {error && (
        <p
          role="alert"
          style={{
            fontSize: '0.85rem',
            color: 'var(--text-danger, #991b1b)',
            margin: 0,
          }}
        >
          {error}
        </p>
      )}
      <div>
        {isSubscribed ? (
          <button
            type="button"
            className="w-btn w-btn-out"
            onClick={() => {
              void unsubscribe();
            }}
            disabled={isPending}
          >
            {isPending ? 'Disabling…' : 'Disable on this device'}
          </button>
        ) : (
          <button
            type="button"
            className="w-btn w-btn-cta"
            onClick={() => {
              void subscribe();
            }}
            disabled={isPending}
          >
            {isPending ? 'Enabling…' : 'Enable push notifications'}
          </button>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="wa-card" style={{ padding: '18px 20px' }}>
      <h2 className="w-lbl" style={{ margin: '0 0 14px' }}>
        {title}
      </h2>
      <div style={{ display: 'grid', gap: 12 }}>{children}</div>
    </section>
  );
}

/* Inline, and that is fine: `auto-fit` over a 14rem floor is LAYOUT, and layout
   is not what a branding round is unable to reach. */
function TwoCol({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span className="w-lbl">{label}</span>
      {children}
    </label>
  );
}

/**
 * 🔴 `.sw` / `.sw-k`, THE SAME SWITCH THE LOT PAGE'S SMART ALERTS USE.
 *
 * It keys on `aria-pressed`, not `aria-checked` — a toggle BUTTON, which is what
 * the site's CSS is written against (`.sw[aria-pressed='true']`). `refined`'s
 * `Switch` was `role="switch"` + `aria-checked` with every colour inline, so it
 * was a second switch that no token change could reach and that went navy while
 * this one goes green.
 */
function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
      <button
        type="button"
        className="sw"
        aria-pressed={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
      >
        <span className="sw-k" />
      </button>
      <span>{label}</span>
    </div>
  );
}

function AlertBox({ variant, children }: { variant: 'danger' | 'success'; children: ReactNode }) {
  const palette =
    variant === 'danger'
      ? {
          bg: 'var(--surface-danger, #fef2f2)',
          fg: 'var(--text-danger, #991b1b)',
          border: 'var(--border-danger, #fecaca)',
        }
      : {
          bg: 'var(--surface-success, #f0fdf4)',
          fg: 'var(--text-success, #166534)',
          border: 'var(--border-success, #bbf7d0)',
        };
  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      style={{
        padding: '0.75rem 1rem',
        borderRadius: 'var(--r-control, 0.375rem)',
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </div>
  );
}
